import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import "./Sending.css";

const DEFAULT_AVATAR =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMiIgeT0iMTIiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Rya2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgo8cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz4KPC9zdmc+Cjwvc3ZnPg==";

function resolveAvatarUrl(userId, avatarFilename) {
  if (!avatarFilename) return DEFAULT_AVATAR;
  if (typeof avatarFilename === "string" && avatarFilename.startsWith("data:")) return avatarFilename;
  if (typeof avatarFilename === "string" && /^https?:\/\//i.test(avatarFilename)) return avatarFilename;
  try {
    const { data } = supabase.storage.from("avatars").getPublicUrl(`${userId}/${avatarFilename}`);
    return data?.publicUrl || DEFAULT_AVATAR;
  } catch {
    return DEFAULT_AVATAR;
  }
}

const asNumber = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

async function fetchWinicoinsBalance(userId) {
  // Prefer wallet_view (if it exists) because it may be decoded already
  try {
    const { data, error } = await supabase
      .from("wallet_view")
      .select("winicoins")
      .eq("user_id", userId)
      .single();
    if (!error && data && data.winicoins != null) return asNumber(data.winicoins);
  } catch {
    // ignore
  }

  // Fallback: wallet table (might be bytea; numeric decode may be handled by view/rls)
  const { data, error } = await supabase
    .from("wallet")
    .select("winicoins")
    .eq("user_id", userId)
    .single();
  if (error || !data) return 0;
  // Best-effort decode
  const raw = data.winicoins;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (raw instanceof Uint8Array) {
    try {
      const decoded = new TextDecoder().decode(raw);
      const parsed = parseFloat(decoded);
      return Number.isFinite(parsed) ? parsed : 0;
    } catch {
      return 0;
    }
  }
  return 0;
}

export default function Sending() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [params] = useSearchParams();
  const recipientId = params.get("to") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [recipient, setRecipient] = useState(null);
  const [balance, setBalance] = useState(0);
  const [feePct, setFeePct] = useState(10);
  const [winiToUsdRate, setWiniToUsdRate] = useState(12);

  const [tab, setTab] = useState("wini"); // "wini" | "currency"
  const [amount, setAmount] = useState("");
  const [feePayer, setFeePayer] = useState("sender"); // "sender" | "recipient"
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      if (!currentUser?.id || !recipientId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const [{ data: p, error: pErr }, { data: s, error: sErr }] = await Promise.all([
          supabase
            .from("profiles")
            .select("user_id, username, display_name, avatar_url")
            .eq("user_id", recipientId)
            .single(),
          supabase.from("app_settings").select("coins_transfer_percentage, wini_to_usd_rate").limit(1).single(),
        ]);

        if (pErr || !p) throw pErr || new Error("Recipient not found");
        const mapped = {
          user_id: p.user_id,
          username: p.username || "user",
          display_name: p.display_name || p.username || "User",
          avatar: resolveAvatarUrl(p.user_id, p.avatar_url),
        };

        const pct = sErr ? 10 : asNumber(s?.coins_transfer_percentage ?? 10);
        const rate = sErr ? 12 : asNumber(s?.wini_to_usd_rate ?? 12);

        const myBal = await fetchWinicoinsBalance(currentUser.id);

        if (cancelled) return;
        setRecipient(mapped);
        setFeePct(pct > 0 ? pct : 0);
        setWiniToUsdRate(rate > 0 ? rate : 12);
        setBalance(myBal);
      } catch (e) {
        console.error("[Sending] boot error:", e);
        if (!cancelled) setError("Failed to load transfer screen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, recipientId]);

  const amountNum = useMemo(() => asNumber(amount), [amount]);
  const feeAmount = useMemo(() => (amountNum * feePct) / 100, [amountNum, feePct]);

  const senderDelta = useMemo(() => {
    if (!amountNum) return 0;
    return feePayer === "sender" ? amountNum + feeAmount : amountNum;
  }, [amountNum, feeAmount, feePayer]);

  const recipientDelta = useMemo(() => {
    if (!amountNum) return 0;
    return feePayer === "recipient" ? Math.max(0, amountNum - feeAmount) : amountNum;
  }, [amountNum, feeAmount, feePayer]);

  const canSubmit = useMemo(() => {
    if (!currentUser?.id || !recipientId) return false;
    if (!amountNum || amountNum <= 0) return false;
    if (senderDelta <= 0) return false;
    if (senderDelta > balance + 1e-9) return false;
    if (!recipientDelta || recipientDelta <= 0) return false;
    if (recipientId === currentUser.id) return false;
    return true;
  }, [amountNum, balance, currentUser?.id, recipientDelta, recipientId, senderDelta]);

  const format = (n, digits = 2) =>
    Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

  const handleTransfer = useCallback(async () => {
    if (!canSubmit || submitting || !currentUser?.id || !recipientId) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      // Re-fetch balances for correctness
      const [senderOld, receiverOld] = await Promise.all([
        fetchWinicoinsBalance(currentUser.id),
        fetchWinicoinsBalance(recipientId),
      ]);

      if (senderDelta > senderOld + 1e-9) {
        setError("Insufficient balance.");
        return;
      }

      const senderNew = Math.max(0, senderOld - senderDelta);
      const receiverNew = receiverOld + recipientDelta;

      const baseId = `tx_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
      const meta = {
        kind: "wallet_transfer",
        original_amount: amountNum,
        fee_percentage: feePct,
        fee_amount: feeAmount,
        fee_payer: feePayer,
      };

      const rows = [
        {
          transaction_id: `${baseId}_out`,
          user_id: currentUser.id,
          source: "transfer",
          flow: "out",
          field: "winicoins",
          delta_factor: -1,
          amount: senderDelta,
          old_balance: senderOld,
          new_balance: senderNew,
          sender_id: currentUser.id,
          receiver_id: recipientId,
          fee_percentage: feePct,
          fee_amount_in_coins: feeAmount,
          description: note?.trim() || null,
          metadata: meta,
        },
        {
          transaction_id: `${baseId}_in`,
          user_id: recipientId,
          source: "transfer",
          flow: "in",
          field: "winicoins",
          delta_factor: 1,
          amount: recipientDelta,
          old_balance: receiverOld,
          new_balance: receiverNew,
          sender_id: currentUser.id,
          receiver_id: recipientId,
          fee_percentage: feePct,
          fee_amount_in_coins: feeAmount,
          description: note?.trim() || null,
          metadata: meta,
        },
      ];

      const { data: inserted, error: insErr } = await supabase.from("transactions").insert(rows).select("id,user_id,flow,amount");
      if (insErr) throw insErr;

      // Notify recipient (best-effort)
      try {
        await supabase.from("notifications").insert([
          {
            title: "You received Winis",
            body: `${format(recipientDelta, 2)} WIN received from a transfer.`,
            notification_type: "wallet_transfer",
            target_user_id: recipientId,
            data: { transaction_ids: (inserted || []).map((r) => r.id), ...meta },
            action_url: "/trans",
            priority: "normal",
            status: "pending",
          },
        ]);
      } catch (e) {
        console.warn("[Sending] notification insert failed:", e);
      }

      setSuccess("Transfer successful.");
      setAmount("");
      setNote("");
      // Go to transactions so user sees it immediately
      setTimeout(() => navigate("/trans"), 700);
    } catch (e) {
      console.error("[Sending] transfer failed:", e);
      setError("Transfer failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [
    amountNum,
    canSubmit,
    currentUser?.id,
    feeAmount,
    feePct,
    feePayer,
    navigate,
    note,
    recipientDelta,
    recipientId,
    senderDelta,
    submitting,
  ]);

  if (loading) {
    return (
      <div className="sending-page">
        <div className="sending-shell">
          <div className="sending-loading">Loading…</div>
        </div>
      </div>
    );
  }

  if (!recipient) {
    return (
      <div className="sending-page">
        <div className="sending-shell">
          <div className="sending-error">{error || "Recipient not found."}</div>
          <button className="sending-backBtn" onClick={() => navigate(-1)}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sending-page">
      <div className="sending-shell">
        <div className="sending-topbar">
          <button className="sending-back" onClick={() => navigate(-1)} aria-label="Back">
            <FiArrowLeft />
          </button>
          <div className="sending-titleWrap">
            <div className="sending-title">Transfer – {recipient.display_name}</div>
          </div>
          <img className="sending-topAvatar" src={recipient.avatar} alt={recipient.display_name} />
        </div>

        <div className="sending-tabs" role="tablist" aria-label="Transfer type">
          <button
            className={`sending-tab ${tab === "wini" ? "active" : ""}`}
            onClick={() => setTab("wini")}
            role="tab"
            aria-selected={tab === "wini"}
          >
            Wini&apos;s
          </button>
          <button
            className={`sending-tab ${tab === "currency" ? "active" : ""}`}
            onClick={() => setTab("currency")}
            role="tab"
            aria-selected={tab === "currency"}
          >
            Currency
          </button>
        </div>

        <div className="sending-section">
          <div className="sending-label">Amount</div>

          <div className="sending-amountRow">
            <input
              className="sending-amountInput"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder="Enter amount"
            />
            <div className="sending-unit">WIN</div>
          </div>

          <div className="sending-available">
            Available: {format(balance, 10)} WIN
          </div>
        </div>

        <div className="sending-section">
          <div className="sending-label">Fee Deduction</div>

          <div className="sending-feePills">
            <button
              className={`sending-pill ${feePayer === "sender" ? "active" : ""}`}
              onClick={() => setFeePayer("sender")}
            >
              From Sender
            </button>
            <button
              className={`sending-pill ${feePayer === "recipient" ? "active" : ""}`}
              onClick={() => setFeePayer("recipient")}
            >
              From Recipient
            </button>
          </div>

          <div className="sending-summary">
            <div className="sending-summaryRow">
              <div className="muted">Transfer fee</div>
              <div>{format(feeAmount, 2)} ({format(feePct, 2)}%)</div>
            </div>
            <div className="sending-summaryRow">
              <div className="muted">You pay</div>
              <div>{format(senderDelta * winiToUsdRate, 2)} US Dollar</div>
            </div>
            <div className="sending-summaryRow">
              <div className="muted">Recipient receives</div>
              <div>{format(recipientDelta * winiToUsdRate, 2)} US Dollar</div>
            </div>
          </div>
        </div>

        <div className="sending-section">
          <div className="sending-label">Description (Optional)</div>
          <textarea
            className="sending-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note about this transfer..."
            rows={3}
          />
        </div>

        {error && <div className="sending-error">{error}</div>}
        {success && <div className="sending-success">{success}</div>}

        <button
          className={`sending-submit ${canSubmit ? "active" : ""}`}
          disabled={!canSubmit || submitting}
          onClick={handleTransfer}
        >
          {submitting ? "Transferring…" : "Transfer  WIN"}
        </button>
      </div>
    </div>
  );
}

